import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { UserPlus, Mail, Lock, User, Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';
import { AuthLayout } from '../components/auth/AuthLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RegisterPage = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', password: '', confirmPassword: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirmPassword) {
      setError('Le password non corrispondono');
      return;
    }
    if (form.password.length < 6) {
      setError('La password deve avere almeno 6 caratteri');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/register`, {
        name: form.name,
        email: form.email,
        password: form.password
      });
      setSuccess('Registrazione completata! Reindirizzamento...');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore durante la registrazione');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => setForm({ ...form, [field]: value });

  return (
    <AuthLayout title="Registrazione" subtitle="Crea il tuo account">
      <form onSubmit={handleSubmit} className="space-y-6" data-testid="register-form">
        {error && (
          <div className="bg-[#E3000F]/10 border border-[#E3000F]/30 p-3 flex items-center gap-2" data-testid="register-error">
            <AlertCircle className="w-4 h-4 text-[#E3000F] flex-shrink-0" />
            <p className="text-sm text-[#E3000F]">{error}</p>
          </div>
        )}
        {success && (
          <div className="bg-[#009246]/10 border border-[#009246]/30 p-3 flex items-center gap-2" data-testid="register-success">
            <CheckCircle className="w-4 h-4 text-[#009246] flex-shrink-0" />
            <p className="text-sm text-[#009246]">{success}</p>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-xs uppercase font-mono text-[#71717A] mb-2">Nome</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
              <input
                type="text"
                value={form.name}
                onChange={(e) => updateField('name', e.target.value)}
                className="w-full bg-[#121212] border border-[#27272A] focus:border-[#E3000F] text-white pl-10 pr-4 py-3 text-sm outline-none"
                placeholder="Nome completo"
                required
                data-testid="register-name-input"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase font-mono text-[#71717A] mb-2">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
              <input
                type="email"
                value={form.email}
                onChange={(e) => updateField('email', e.target.value)}
                className="w-full bg-[#121212] border border-[#27272A] focus:border-[#E3000F] text-white pl-10 pr-4 py-3 text-sm outline-none"
                placeholder="email@esempio.it"
                required
                data-testid="register-email-input"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase font-mono text-[#71717A] mb-2">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={(e) => updateField('password', e.target.value)}
                className="w-full bg-[#121212] border border-[#27272A] focus:border-[#E3000F] text-white pl-10 pr-10 py-3 text-sm outline-none"
                placeholder="Minimo 6 caratteri"
                required
                data-testid="register-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-white"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase font-mono text-[#71717A] mb-2">Conferma Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={form.confirmPassword}
                onChange={(e) => updateField('confirmPassword', e.target.value)}
                className="w-full bg-[#121212] border border-[#27272A] focus:border-[#E3000F] text-white pl-10 pr-4 py-3 text-sm outline-none"
                placeholder="Ripeti la password"
                required
                data-testid="register-confirm-password-input"
              />
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-[#E3000F] hover:bg-[#B8000C] disabled:opacity-50 text-white py-3 font-bold uppercase tracking-wider flex items-center justify-center gap-2 transition-colors"
          data-testid="register-form-submit-button"
        >
          <UserPlus className="w-4 h-4" />
          {loading ? 'REGISTRAZIONE...' : 'REGISTRATI'}
        </button>

        <div className="text-center text-sm">
          <Link to="/login" className="text-[#71717A] hover:text-white transition-colors">
            Hai già un account? <span className="text-[#E3000F]">Accedi</span>
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
};

export default RegisterPage;
